const API_URL = process.env.REACT_APP_API_URL ?? '';
const TOKEN_STORAGE_KEY = 'ffom-auth-token';
const ROLE_STORAGE_KEY = 'ffom-auth-role';
const TENANT_NAME_STORAGE_KEY = 'ffom-auth-tenant-name';
const CAN_MANAGE_TENANTS_STORAGE_KEY = 'ffom-auth-can-manage-tenants';

export type Role = 'admin' | 'staff' | 'superadmin';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(ROLE_STORAGE_KEY);
  localStorage.removeItem(TENANT_NAME_STORAGE_KEY);
  localStorage.removeItem(CAN_MANAGE_TENANTS_STORAGE_KEY);
}

export function getRole(): Role | null {
  const role = localStorage.getItem(ROLE_STORAGE_KEY);
  return role === 'admin' || role === 'staff' || role === 'superadmin' ? role : null;
}

function setRole(role: Role): void {
  localStorage.setItem(ROLE_STORAGE_KEY, role);
}

export function getTenantName(): string | null {
  return localStorage.getItem(TENANT_NAME_STORAGE_KEY);
}

function setTenantName(tenantName: string | undefined): void {
  if (tenantName) {
    localStorage.setItem(TENANT_NAME_STORAGE_KEY, tenantName);
  } else {
    localStorage.removeItem(TENANT_NAME_STORAGE_KEY);
  }
}

// True for a brigade staff/admin whose email is *also* a superadmin — lets
// them reach the cross-tenant Tenants screen without superadmin being a
// separate role that locks them out of their own brigade's order app.
export function getCanManageTenants(): boolean {
  return localStorage.getItem(CAN_MANAGE_TENANTS_STORAGE_KEY) === 'true';
}

function setCanManageTenants(canManageTenants: boolean | undefined): void {
  if (canManageTenants) {
    localStorage.setItem(CAN_MANAGE_TENANTS_STORAGE_KEY, 'true');
  } else {
    localStorage.removeItem(CAN_MANAGE_TENANTS_STORAGE_KEY);
  }
}

export async function loginWithGoogle(
  credential: string
): Promise<{ token: string; role: Role; tenantName?: string; isSuperadmin?: boolean }> {
  const res = await fetch(`${API_URL}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  if (!res.ok) {
    const body: { message?: string } = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Login failed: ${res.status}`);
  }
  const data: { token: string; role: Role; tenantName?: string; isSuperadmin?: boolean } = await res.json();
  setToken(data.token);
  setRole(data.role);
  setTenantName(data.tenantName);
  setCanManageTenants(data.isSuperadmin);
  return data;
}
